import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { BikeCard } from "@/components/BikeCard";
import { LogoMark } from "@/components/Logo";
import { Shield, Zap, Clock, MapPin, Headphones, BadgeCheck, ArrowRight, ArrowUpRight, Star, ChevronRight, Users, Wrench, Trophy, Bike, Truck, GraduationCap, Briefcase, Building2, Building, Plane, Package } from "lucide-react";
import { api } from "@/lib/api";
import { CONTACT } from "@/constants/contact";
import { FleetEnquiryModal } from "@/components/FleetEnquiryModal";

const FEATURES = [
  { icon: Shield, title: "Insurance included", desc: "Every ride covered with comprehensive damage and theft insurance." },
  { icon: Headphones, title: "24×7 roadside support", desc: "Stranded? Tap once. Our team reaches you in under 45 minutes." },
  { icon: Zap, title: "Electric-first fleet", desc: "Premium EVs from Ather, Ola, Vida & more. Zero fuel costs." },
  { icon: BadgeCheck, title: "Verified, sanitised", desc: "Every bike inspected on a 47-point checklist before handover." },
];

const PERFECT_FOR = [
  { icon: Bike, label: "Daily commuters" },
  { icon: Truck, label: "Delivery partners" },
  { icon: GraduationCap, label: "Students" },
  { icon: Briefcase, label: "Corporate employees" },
  { icon: Building2, label: "Hotels & resorts" },
  { icon: Building, label: "Businesses & fleets" },
  { icon: Plane, label: "Tourists & travelers" },
  { icon: Package, label: "Last-mile delivery" },
];

const TESTIMONIALS = [
  { name: "Aarav Mehta", city: "Bangalore", quote: "Picked up an Ather 450X in 4 minutes flat. Felt like a concierge service, not a rental.", rating: 5 },
  { name: "Sneha Kapoor", city: "Goa", quote: "Best decision for our Goa trip. The Royal Enfield was in pristine condition and the doorstep delivery was a game changer.", rating: 5 },
  { name: "Rohit Iyer", city: "Mumbai", quote: "Used eBikeGo for two months as my daily commute. Cheaper than buying, zero maintenance headache.", rating: 5 },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [cities, setCities] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [fleetEnquiryOpen, setFleetEnquiryOpen] = useState(false);

  useEffect(() => {
    // Featured: pull models flagged as featured (8), deduplicated by name so we show one per model
    api.get("/bikes", { params: { featured: true, sort: "rating" } }).then((r) => {
      const seen = new Set();
      const dedup = [];
      for (const b of r.data) {
        const key = `${b.brand}::${b.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        dedup.push(b);
        if (dedup.length >= 8) break;
      }
      setFeatured(dedup);
    });
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/categories").then((r) => setCategoriesData(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* HERO - DARK with brand bg image */}
      <div className="relative bg-[#0A0B10] text-white overflow-hidden">
        <Navbar variant="dark" />

        {/* Background scooter image */}
        <div
          className="absolute inset-0 bg-no-repeat bg-cover bg-right md:bg-[right_-4rem_center] lg:bg-right"
          style={{
            backgroundImage: "url(https://customer-assets.emergentagent.com/job_ebikego-preview/artifacts/ay0lkhhr_home%20page%20bg.png)",
          }}
        />
        {/* Left-side darken gradient so text is legible */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B10] via-[#0A0B10]/85 md:via-[#0A0B10]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-pattern opacity-20" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-10">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center min-h-[460px]">
            <div className="fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono-num uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3DDC97] animate-pulse" />
                100% Electric Mobility
              </div>
              <h1 className="font-display font-extrabold text-5xl md:text-7xl lg:text-[88px] leading-[0.95] mt-6 tracking-tight">
                Charge in.<br />
                <span className="brand-gradient">Ride electric.</span>
              </h1>
              <p className="text-lg text-white/70 mt-6 max-w-lg leading-relaxed">
                Rent from eBikeGo's owned electric fleet with flexible hourly, daily, weekly, or monthly plans. Insured rides and doorstep delivery.
              </p>
              <div className="flex items-center gap-6 mt-8">
                <div>
                  <p className="font-display font-extrabold text-3xl">100+</p>
                  <p className="text-xs text-white/50 uppercase tracking-widest">Electric Vehicles</p>
                </div>
                <div className="h-12 w-px bg-white/10" />
                <div>
                  <p className="font-display font-extrabold text-3xl">5</p>
                  <p className="text-xs text-white/50 uppercase tracking-widest">Launch Cities</p>
                </div>
                <div className="h-12 w-px bg-white/10" />
                <div>
                  <p className="font-display font-extrabold text-3xl">24×7</p>
                  <p className="text-xs text-white/50 uppercase tracking-widest">Customer Support</p>
                </div>
              </div>
            </div>

            {/* Floating info badges (positioned over the hero image area) */}
            <div className="relative fade-up hidden lg:block" style={{ animationDelay: "120ms" }}>
              <div className="absolute top-0 right-0 glass-dark rounded-2xl p-4 w-48 animate-float">
                <p className="text-[10px] uppercase tracking-widest text-white/50">Live · Featured</p>
                <p className="font-display font-bold text-lg mt-1">eBikeGo Smart</p>
                <p className="text-xs text-[#3DDC97] mt-1 flex items-center gap-1"><Zap size={11} /> 90 km range</p>
              </div>
              <div className="absolute bottom-12 right-0 glass-dark rounded-2xl p-4 animate-float" style={{ animationDelay: "1s" }}>
                <div className="flex items-center gap-2">
                  <LogoMark size={22} />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/50">Quick Start</p>
                    <p className="text-sm font-bold">Door Step Delivery</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Search bar sits inside dark hero, no overlap */}
          <div className="mt-8 lg:mt-12 relative z-20">
            <SearchBar variant="dark" />
          </div>
        </div>
      </div>

      {/* City marquee */}
      <section className="py-12 border-y border-black/5 overflow-hidden">
        <div className="flex items-center gap-12 animate-marquee whitespace-nowrap">
          {[...cities, ...cities, ...cities].map((c, i) => (
            <div key={i} className="flex items-center gap-3 text-gray-400">
              <MapPin size={16} />
              <span className="font-display font-bold text-2xl">{c.name}</span>
              <span className="w-2 h-2 rounded-full bg-[#3DDC97]" />
            </div>
          ))}
        </div>
      </section>

      {/* EXPLORE EV CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">— EXPLORE EVs</p>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Browse by vehicle type.</h2>
            <p className="text-gray-600 mt-3 max-w-lg">Four flavours of clean mobility — pick what matches your ride.</p>
          </div>
          <Link to="/bikes" data-testid="view-all-categories" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold hover:text-[#2D7FF9]">
            All EVs <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="ev-categories-grid">
          {categoriesData.map((cat, i) => (
            <Link
              key={cat.slug}
              to={`/bikes?category=${cat.slug}`}
              data-testid={`category-card-${cat.slug}`}
              className="card-premium group fade-up flex flex-col"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="relative aspect-[5/4] overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[11px] font-bold text-ink flex items-center gap-1">
                  <span className="font-mono-num">{cat.count}</span> models
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-display font-bold text-xl leading-tight">{cat.name}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed flex-1">{cat.subtitle}</p>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="font-semibold text-ink">Explore Vehicles</span>
                  <ArrowUpRight size={16} className="text-gray-400 group-hover:text-[#2D7FF9] group-hover:rotate-45 transition-all" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured bikes */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">— POPULAR</p>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Popular electric vehicles.</h2>
          </div>
          <Link to="/bikes" data-testid="view-all-bikes" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold hover:text-[#2D7FF9]">
            Explore All Electric Vehicles <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((b, i) => <BikeCard key={b.id} bike={b} index={i} />)}
        </div>
        <div className="mt-10 flex justify-center md:hidden">
          <Link to="/bikes" className="btn-primary">Explore All Electric Vehicles <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* PERFECT FOR — audience segments */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-12" data-testid="perfect-for-section">
        <div className="rounded-[28px] border border-[#2D7FF9]/20 bg-gradient-to-br from-white to-[#EAF2FF] p-6 md:p-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-[#3DDC97]" />
            <p className="text-[11px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">Perfect For</p>
          </div>
          <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight text-ink">
            Who is eBikeGo rental for?
          </h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
            {PERFECT_FOR.map((p, i) => (
              <div
                key={p.label}
                data-testid={`perfect-for-${i}`}
                className="rounded-2xl bg-white border border-[#2D7FF9]/10 shadow-[0_1px_2px_rgba(45,127,249,0.04)] hover:shadow-[0_8px_20px_rgba(45,127,249,0.08)] hover:-translate-y-0.5 transition-all p-4 md:p-5 flex flex-col items-center text-center gap-3"
              >
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-[#EAF2FF] flex items-center justify-center text-[#2D7FF9]">
                  <p.icon size={22} strokeWidth={1.75} />
                </div>
                <p className="text-xs md:text-sm font-semibold text-ink leading-tight">
                  {p.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B / Fleet Rental banner */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14" data-testid="b2b-fleet-banner">
        <div className="relative overflow-hidden rounded-[32px] bg-[#0A0B10] text-white p-8 md:p-14 lg:p-16">
          {/* Ambient glows */}
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#3DDC97]/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#2D7FF9]/25 blur-3xl" />
          <div className="absolute inset-0 grid-pattern opacity-25" />
          <div className="absolute inset-0 noise" />

          <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#3DDC97]/30 bg-[#3DDC97]/10 px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3DDC97]" />
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#3DDC97] font-mono-num">
                  B2B / Fleet Rental
                </p>
              </div>
              <h2 className="font-display font-extrabold text-3xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.05]">
                Power your business with{" "}
                <span className="brand-gradient">eBikeGo Fleet Rentals.</span>
              </h2>
              <p className="text-white/70 mt-5 text-[15px] md:text-lg leading-relaxed max-w-xl">
                Scalable, reliable, sustainable. Perfect for companies,
                delivery operations, corporate travel & more.
              </p>

              <ul className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
                {[
                  "Custom plans",
                  "Priority support",
                  "Easy onboarding",
                  "24×7 assistance",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-white/85">
                    <span className="w-5 h-5 rounded-full bg-[#3DDC97]/20 text-[#3DDC97] flex items-center justify-center shrink-0 text-[11px] font-bold">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setFleetEnquiryOpen(true)}
                  data-testid="b2b-enquire-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#3DDC97] text-[#0A0B10] font-bold px-7 py-4 hover:opacity-90 transition-opacity"
                >
                  Enquire Fleet Solutions
                  <ArrowRight
                    size={17}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
                <Link
                  to="/business-fleet"
                  data-testid="b2b-learn-more-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-semibold px-7 py-4 hover:border-white/50 transition-colors"
                >
                  Learn more
                </Link>
              </div>
            </div>

            {/* Right side: stats card cluster */}
            <div className="relative hidden lg:block">
              <div className="glass-dark rounded-3xl p-6 relative">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#3DDC97] animate-pulse" />
                  <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold font-mono-num">
                    Fleet Snapshot
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  {[
                    { k: "500+", v: "Vehicles deployed" },
                    { k: "50+", v: "Partner brands" },
                    { k: "24/7", v: "Assistance SLA" },
                    { k: "5", v: "Launch cities" },
                  ].map((s) => (
                    <div
                      key={s.v}
                      className="rounded-2xl bg-white/[0.04] border border-white/10 p-4"
                    >
                      <p className="font-display font-extrabold text-2xl md:text-3xl brand-gradient">
                        {s.k}
                      </p>
                      <p className="text-[11px] text-white/60 mt-1 leading-snug">
                        {s.v}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="absolute -bottom-6 -right-4 glass-dark rounded-2xl px-4 py-3 animate-float"
                style={{ animationDelay: "0.6s" }}
              >
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">
                  Corporate & delivery ready
                </p>
                <p className="text-sm font-bold mt-1">Custom onboarding in 48 hrs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities grid */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="mb-12">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">— CITIES</p>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Available where you ride.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {cities.map((c) => (
            <Link key={c.slug} to={`/bikes?city=${c.slug}`} data-testid={`city-card-${c.slug}`} className="group relative aspect-[3/4] rounded-2xl overflow-hidden border border-gray-200 hover:border-[#3DDC97] transition-colors">
              <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <p className="font-display font-bold text-white text-lg">{c.name}</p>
                <p className="text-white/70 text-xs flex items-center gap-1"><ChevronRight size={11} />Explore</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">— WHY EBIKEGO</p>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Built for the modern rider.</h2>
            <p className="text-gray-600 mt-5 leading-relaxed max-w-md">
              We obsess over the details so your ride is effortless from booking to drop-off.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="card-premium p-6">
                <div className="w-11 h-11 rounded-xl bg-brand-gradient flex items-center justify-center text-white">
                  <f.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg mt-4">{f.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works - dark */}
      <section className="bg-[#0A0B10] text-white py-24 my-12 mx-5 md:mx-8 rounded-[40px] relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-12">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#3DDC97] font-mono-num">— PROCESS</p>
          <h2 className="font-display font-extrabold text-4xl md:text-6xl mt-3 tracking-tight max-w-2xl">Three steps. Ready to roll.</h2>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              { n: "01", t: "Pick your bike", d: "Browse 12K+ verified bikes across 6 cities. Filter by category, fuel, price." },
              { n: "02", t: "Set your dates", d: "Hourly, daily, weekly or monthly plans. Pick where you want it delivered." },
              { n: "03", t: "Ride free", d: "Helmet & insurance included. We'll handle the rest while you focus on the road." },
            ].map((s, i) => (
              <div key={i} className="border-t border-white/10 pt-6">
                <p className="font-mono-num text-[#3DDC97] font-bold text-sm">{s.n}</p>
                <h3 className="font-display font-bold text-2xl mt-3">{s.t}</h3>
                <p className="text-white/60 text-sm mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT IN NUMBERS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 relative">
        <div className="text-center mb-16">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">— TEN YEARS · INDIA</p>
          <h2 className="font-display font-extrabold text-5xl md:text-6xl mt-3 tracking-tight">
            Our impact in <span className="brand-gradient">numbers</span>.
          </h2>
          <p className="text-gray-600 mt-4 max-w-xl mx-auto">A decade of obsession with mobility. Here's what it adds up to.</p>
        </div>

        {/* Bento grid: stats + polaroid photos */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {/* Stat 1 — Fleet */}
          <div className="col-span-2 md:col-span-2 card-premium p-7 flex flex-col justify-between min-h-[200px] relative overflow-hidden" data-testid="impact-fleet">
            <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-[#3DDC97]/10" />
            <div className="relative">
              <Wrench className="text-[#2D7FF9]" size={22} />
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-4">Regularly serviced</p>
              <p className="font-display font-extrabold text-3xl md:text-4xl mt-1 tracking-tight">FLEET</p>
            </div>
            <p className="relative text-sm text-gray-600 mt-3">47-point safety inspection on every bike, every time.</p>
          </div>

          {/* Polaroid 1 */}
          <div className="hidden md:block relative aspect-[3/4] rounded-2xl overflow-hidden group">
            <img src="https://images.pexels.com/photos/9168370/pexels-photo-9168370.jpeg?auto=compress&w=600" alt="rider" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Polaroid 2 */}
          <div className="hidden md:block relative aspect-[3/4] rounded-2xl overflow-hidden group">
            <img src="https://images.pexels.com/photos/1413412/pexels-photo-1413412.jpeg?auto=compress&w=600" alt="rider" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Stat 2 — 100+ EVs (highlighted) */}
          <div className="col-span-2 card-premium p-7 flex flex-col justify-between min-h-[200px] relative overflow-hidden bg-gradient-to-br from-[#3DDC97]/10 to-[#2D7FF9]/10 border-[#3DDC97]/30" data-testid="impact-bikes">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">On the road</p>
              <p className="font-display font-extrabold text-5xl md:text-6xl mt-1 brand-gradient tracking-tight font-mono-num">100+</p>
            </div>
            <p className="text-sm font-bold text-ink mt-3 uppercase tracking-widest">Electric Vehicles</p>
          </div>

          {/* Polaroid 3 */}
          <div className="hidden lg:block relative aspect-[3/4] rounded-2xl overflow-hidden group row-start-2 lg:row-start-auto">
            <img src="https://images.unsplash.com/photo-1622185135505-2d795003994a?w=600" alt="rider" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Polaroid 4 */}
          <div className="hidden md:block relative aspect-[3/4] rounded-2xl overflow-hidden group">
            <img src="https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=600" alt="rider" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Stat 3 — 100% Electric */}
          <div className="col-span-2 card-premium p-7 flex flex-col justify-between min-h-[200px] relative overflow-hidden" data-testid="impact-years">
            <div className="absolute -left-8 -top-8 w-32 h-32 rounded-full bg-[#2D7FF9]/10" />
            <div className="relative">
              <Trophy className="text-[#3DDC97]" size={22} />
              <p className="font-display font-extrabold text-5xl md:text-6xl mt-2 tracking-tight font-mono-num">100%</p>
            </div>
            <p className="relative text-sm font-bold text-ink uppercase tracking-widest">Electric Fleet</p>
          </div>

          {/* Polaroid 5 */}
          <div className="hidden lg:block relative aspect-[3/4] rounded-2xl overflow-hidden group">
            <img src="https://images.unsplash.com/photo-1580310614729-ccd69652491d?w=600" alt="rider" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Stat 4 — 5 Launch Cities */}
          <div className="col-span-2 card-premium p-7 flex flex-col justify-between min-h-[200px] relative overflow-hidden" data-testid="impact-cities">
            <div className="relative">
              <MapPin className="text-[#2D7FF9]" size={22} />
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-4">Now live in</p>
              <p className="font-display font-extrabold text-6xl md:text-7xl mt-1 tracking-tight font-mono-num">5</p>
            </div>
            <p className="text-sm font-bold text-ink mt-3 uppercase tracking-widest">Launch Cities</p>
          </div>

          {/* Polaroid 6 */}
          <div className="hidden md:block relative aspect-[3/4] rounded-2xl overflow-hidden group">
            <img src="https://images.pexels.com/photos/2519374/pexels-photo-2519374.jpeg?auto=compress&w=600" alt="rider" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Stat 5 — Growing Community (highlighted) */}
          <div className="col-span-2 md:col-span-3 lg:col-span-3 rounded-[20px] border border-[#0A0B10] p-7 flex flex-col md:flex-row md:items-center justify-between min-h-[200px] relative overflow-hidden bg-[#0A0B10] text-white" data-testid="impact-users">
            <div className="absolute inset-0 grid-pattern opacity-30" />
            <div className="relative">
              <Users className="text-[#3DDC97]" size={22} />
              <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mt-4">Our riders</p>
              <p className="font-display font-extrabold text-4xl md:text-5xl mt-1 brand-gradient tracking-tight">Growing Community</p>
              <p className="text-sm font-bold mt-2 uppercase tracking-widest">And counting, every day.</p>
            </div>
            <Link to="/bikes" data-testid="impact-cta" className="relative btn-primary mt-6 md:mt-0 shrink-0">
              Join the ride <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">— TESTIMONIALS</p>
        <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight max-w-2xl">Riders who fell in love.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="card-premium p-7" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1">{Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={14} className="fill-[#3DDC97] text-[#3DDC97]" />)}</div>
              <p className="text-ink mt-5 leading-relaxed text-[15px]">"{t.quote}"</p>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t border-gray-100">
                <div className="w-10 h-10 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.city}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div className="bg-brand-gradient rounded-[40px] p-12 md:p-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 noise" />
          <div className="relative">
            <h2 className="font-display font-extrabold text-4xl md:text-6xl text-white tracking-tight">Ready to ride?</h2>
            <p className="text-white/90 mt-4 text-lg max-w-xl mx-auto">Your next adventure is one tap away. Join 2.4M riders who've made the switch.</p>
            <Link to="/bikes" data-testid="cta-bikes-btn" className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-[#0A0B10] text-white font-bold hover:bg-black/80 transition-colors">
              Browse bikes <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <FleetEnquiryModal open={fleetEnquiryOpen} onClose={() => setFleetEnquiryOpen(false)} />
      <Footer />
    </div>
  );
};

export default Home;
